import { useEffect, useState } from "react";
import { Instagram, MessageCircle, Phone } from "lucide-react";
import { INSTAGRAM_URL, PHONE_TEL, WHATSAPP_URL } from "@/data/site";
import { scrollToSection } from "@/lib/scroll";

const NAV = [
    { label: "Occasions", href: "#occasions", id: "header-nav-occasions" },
    { label: "Gallery", href: "#gallery", id: "header-nav-gallery" },
    { label: "Reviews", href: "#reviews", id: "header-nav-reviews" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, href) => {
        e.preventDefault();
        scrollToSection(href);
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-cream/90 shadow-[0_1px_0_rgba(244,184,199,0.4)] backdrop-blur-md"
                    : "bg-cream/60 backdrop-blur-sm"
            }`}
        >
            <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:h-[72px] sm:px-6">
                <a
                    href="#home"
                    data-testid="header-logo-link"
                    onClick={(e) => go(e, "#home")}
                    className="flex items-center gap-2.5"
                >
                    <img
                        src="/images/logo.jpg"
                        alt="Cakes'alot by Indu logo"
                        className="size-10 rounded-full ring-1 ring-blush/60 sm:size-11"
                    />
                    <span className="leading-none">
                        <span className="block font-serif text-lg font-bold text-cocoa sm:text-xl">
                            Cakes'alot
                        </span>
                        <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-[0.3em] text-leaf">
                            by Indu
                        </span>
                    </span>
                </a>

                <nav className="hidden items-center gap-8 md:flex">
                    {NAV.map((item) => (
                        <a
                            key={item.href}
                            href={item.href}
                            data-testid={item.id}
                            onClick={(e) => go(e, item.href)}
                            className="text-sm font-medium text-cocoa-soft transition-colors hover:text-leaf"
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <a
                        data-testid="header-call-btn"
                        href={PHONE_TEL}
                        aria-label={`Call ${"077-8504401"}`}
                        className="icon-btn"
                    >
                        <Phone className="size-4" />
                    </a>
                    <a
                        data-testid="header-instagram-btn"
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Cakes'alot on Instagram"
                        className="icon-btn hidden sm:inline-flex"
                    >
                        <Instagram className="size-4" />
                    </a>
                    <a
                        data-testid="header-whatsapp-btn"
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-10 items-center gap-2 rounded-full bg-leaf px-4 text-sm font-semibold text-cream transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-leaf/25 active:translate-y-0"
                    >
                        <MessageCircle className="size-4" />
                        <span className="hidden sm:inline">WhatsApp</span>
                    </a>
                </div>
            </div>
        </header>
    );
}
